import { WINDOW_HEIGHT, WINDOW_WIDTH } from './constants';
import { GroundType } from '../../../models/ground';
import { GenericValueType } from '../../../query/track';

export function findMinMaxX(pathData) {
  const xData = pathData.map((e) => parseFloat(e.x)).filter((x) => !Number.isNaN(x));
  return { minX: Math.min(...xData), maxX: Math.max(...xData) };
}

export function findMinMaxY(pathData) {
  const yData = pathData.map((e) => parseFloat(e.y)).filter((y) => !Number.isNaN(y));
  return { minY: Math.min(...yData), maxY: Math.max(...yData) };
}

export function getScale(width, height, boxWidth, boxHeight) {
  const scaleX = (boxWidth || WINDOW_WIDTH) / width;
  const scaleY = (boxHeight || WINDOW_HEIGHT) / height;
  return Math.min(scaleX, scaleY);
}
export function isGroundVertical(groundData) {
  return (
    (groundData.vertexBX.value - groundData.vertexAX.value) ** 2 + (groundData.vertexBY.value - groundData.vertexAY.value) ** 2
    > (groundData.vertexCX.value - groundData.vertexBX.value) ** 2 + (groundData.vertexCY.value - groundData.vertexBY.value) ** 2
  );
}
export function computeAngle(groundData) {
  if (groundData.vertexAX.value === groundData.vertexBX.value) return 0;
  const q = (groundData.vertexAX.value * groundData.vertexBY.value
      - groundData.vertexBX.value * groundData.vertexAY.value)
    / (groundData.vertexAX.value - groundData.vertexBX.value);

  const side1 = q - groundData.vertexAY.value;
  const side2 = groundData.vertexAX.value;

  let angle = side1 === 0 ? 0 : Math.atan(side2 / side1);

  if (isGroundVertical(groundData)) angle += Math.PI / 2;
  return angle;
}
export function evaluateXFlip(groundData) {
  const aX = 0;
  const aY = 0;
  const cX = groundData.vertexCX.value - Math.abs(groundData.vertexAX.value);
  const bY = groundData.vertexBY.value - Math.abs(groundData.vertexAY.value);
  return aX > cX && aY > bY;
}
export function evaluateYFlip(groundData) {
  const aX = 0;
  const aY = 0;
  const cX = groundData.vertexCX.value - Math.abs(groundData.vertexAX.value);
  const bY = groundData.vertexBY.value - Math.abs(groundData.vertexAY.value);
  return aX < cX && aY < bY;
}
export function flipPoint(p, groundData) {
  const point = p;
  if (groundData) {
    if (evaluateXFlip(groundData)) point.x *= -1;
    // if (evaluateYFlip(groundData)) point.y *= -1;
  }
  return point;
}
export function rotate(point, angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const nx = cos * point.x - sin * point.y;
  const ny = cos * point.y + sin * point.x;
  return { x: nx, y: ny };
}
export function flipX(p, o) {
  return o.x + (p.x - o.x) * Math.cos(Math.PI) - (p.y - o.y) * Math.sin(Math.PI);
}
export function flipY(p, o) {
  return o.y + (p.x - o.x) * Math.sin(Math.PI) + (p.y - o.y) * Math.cos(Math.PI);
}
export function convertDegrees(angleInRadians) {
  return (angleInRadians * 180.0) / Math.PI;
}

/**
 * Converts an angle from degrees to radians.
 *
 * @param angleInDegrees The angle value in degrees to be converted to radians.
 * @return The angle value in radians.
 */
export function convertRadians(angleInDegrees) {
  return (angleInDegrees * Math.PI) / 180.0;
}

/**
 * Converts polar coordinates to Cartesian coordinates.
 *
 * @param centerX The x-coordinate of the center point.
 * @param centerY The y-coordinate of the center point.
 * @param radius The distance from the center to the point.
 * @param angleInDegrees The angle in degrees relative to the center.
 * @return An object containing the Cartesian coordinates with properties `x` and `y`.
 */
export function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = convertRadians(angleInDegrees - 90);

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

/**
 * Generates an SVG path data string that describes an arc.
 *
 * @param x The x-coordinate of the arc's center.
 * @param y The y-coordinate of the arc's center.
 * @param radius The radius of the arc.
 * @param startAngle The starting angle of the arc in degrees.
 * @param endAngle The ending angle of the arc in degrees.
 * @return A string representing the path data for the SVG arc.
 */
export function describeArc(x, y, radius, startAngle, endAngle) {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y].join(' ');

  return d;
}

/**
 * Generates a path description string for an arc based on the provided parameters.
 *
 * @param x The x-coordinate of the arc's starting point.
 * @param y The y-coordinate of the arc's starting point.
 * @param radius The radius of the arc.
 * @param xEnd The x-coordinate of the arc's ending point.
 * @param yEnd The y-coordinate of the arc's ending point.
 * @return A string representing the path description for the arc.
 */
export function describeArcBase(x, y, radius, xEnd, yEnd) {
  const d = ['M', x, y, 'A', radius, radius, 0, 0, 1, xEnd, yEnd].join(' ');

  return d;
}

/**
 * Generates the SVG path data string for an arc or circular sector.
 *
 * @param x The x-coordinate of the center of the arc.
 * @param y The y-coordinate of the center of the arc.
 * @param radius The radius of the arc.
 * @param startAngle The starting angle of the arc in degrees.
 * @param endAngle The ending angle of the arc in degrees.
 * @param isSector Indicates whether the path should represent a sector (arc with lines connecting to the center).
 * @return The SVG path data string for the arc or sector.
 */
export function getArcPath(x, y, radius, startAngle, endAngle, isSector = false) {
  const start = polarToCartesian(x, y, radius, startAngle);
  const end = polarToCartesian(x, y, radius, endAngle);

  const angleDiff = (endAngle - startAngle + 360) % 360;
  const largeArcFlag = angleDiff > 180 ? 1 : 0;

  const d = [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y,
  ];

  if (isSector) {
    d.push('L', x, y, 'Z');
  }

  return d.join(' ');
}

export function calculateRealWorldMeter(width, vertexCXVAlue) {
  return vertexCXVAlue ? width / vertexCXVAlue : 0;
}

export function vertexStringToFloat(vertex: GenericValueType<number> | undefined) {
  return vertex?.value
    ? { ...vertex, value: parseFloat(vertex.value.toString()) }
    : vertex;
}

export function getPointsDistance(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx ** 2 + dy ** 2);
}

export function convertEditDataToFloat(editData: Partial<GroundType>) {
  return {
    groundType: editData.groundType,
    groundCoordsType: editData.groundCoordsType,
    groundSurfaceColor: editData.groundSurfaceColor,
    vertexAX: vertexStringToFloat(editData.vertexAX),
    vertexAY: vertexStringToFloat(editData.vertexAY),
    vertexBX: vertexStringToFloat(editData.vertexBX),
    vertexBY: vertexStringToFloat(editData.vertexBY),
    vertexCX: vertexStringToFloat(editData.vertexCX),
    vertexCY: vertexStringToFloat(editData.vertexCY),
    vertexDX: vertexStringToFloat(editData.vertexDX),
    vertexDY: vertexStringToFloat(editData.vertexDY),
  };
}
